import {IS_LOGGED_IN, IS_STAGING, PLAYER_ID, USE_NEW_BANKING_EVENT_LISTENER} from "Setup/config";
import {sendGA4Event} from "Helpers/gaEvents";
import {sendLogglyEvent} from "Helpers/loggly";
import ErrorBoundary from "Components/ErrorBoundary";
import {useEffect, useRef} from "react";
import useBanking from "Hooks/api/useBanking";
import {setHasDeposited} from "Redux/actions/morpheus";
import {useDispatch} from "react-redux";
import useBankingTracking from "Hooks/api/useBankingTracking";
import {sleep} from "Helpers/async";

const sendMessagesFromSafechargeToLoggly = true;
const sendLogglyEventBankingEventListener = (message, json) => sendMessagesFromSafechargeToLoggly ? sendLogglyEvent('Safecharge', message, json) : null;

const _BankingEventListener = ({hasError}) => {
    const dispatch = useDispatch();
    const {hasDeposited} = useBanking({});
    const {sendDepositTrackingEvent, sendWithdrawalTrackingEvent} = useBankingTracking();
    const isFTDRef = useRef(!hasDeposited);

    if (hasError) return null;
    if (!IS_LOGGED_IN || !USE_NEW_BANKING_EVENT_LISTENER) return null;

    const _setHasDeposited = () => {
        if (isFTDRef.current) {
            dispatch(setHasDeposited());
            isFTDRef.current = false;
        }
    };

    const listenForBankingWindowEvents = async (evt) => {
        if (evt.origin.includes("safecharge") && evt?.data) {
            try {
                const parsedMessage = JSON.parse(evt.data);
                console.log(parsedMessage);

                if (parsedMessage) {
                    // Catch successful withdrawal requests
                    if(parsedMessage?.payload === "successfulWithdrawalRequest" && parsedMessage?.amount){
                        sendWithdrawalTrackingEvent(true, parsedMessage?.amount);
                    }

                    // Catch successful and failed deposit events
                    if (parsedMessage?.payload?.action) {
                        const newAction = parsedMessage.payload.action;
                        const successfulActionsToWatch = ["made deposit", "made first deposit"];
                        const failActionsToWatch = ["failed  deposit", "failed  first deposit"];

                        let depositValue = 0;
                        if (parsedMessage?.payload?.deposit_amount) {
                            depositValue = Number(Number(parsedMessage.payload.deposit_amount).toFixed(2));

                            console.log(`%cSuccessful Deposit : £${depositValue}`, 'color: green;');
                            console.log(parsedMessage);
                        }

                        if (successfulActionsToWatch.includes(newAction)) {
                            const transactionId = parsedMessage?.payload?.reference ?? null;
                            const depositPayload = {
                                currency: 'GBP',
                                player_id: PLAYER_ID,
                                value: depositValue,
                                ...(transactionId && {transaction_id: transactionId}),
                            }

                            if (isFTDRef.current) {
                                if (!IS_STAGING) {
                                    window.dataLayer.push({
                                        event: 'firstDeposit_v2',
                                        ...(transactionId && {transaction_id: transactionId})
                                    });

                                    sendGA4Event('purchase_FTD', depositPayload);
                                }
                            }else{
                                sendGA4Event('subsequent_deposit', depositPayload);
                            }

                            sendGA4Event('purchase', depositPayload);

                            sendDepositTrackingEvent(true, depositValue);
                            sendLogglyEventBankingEventListener(`${isFTDRef.current ? 'First ' : ''}Deposit from the banking client`, parsedMessage);
                            _setHasDeposited();

                            // Added a short wait here to improve event tracking.
                            console.log('Waiting.....');
                            await sleep(1500);
                            // Send an event for the banking modal to pick up. These are then used to determine
                            // whether or not to show the PostFreeSpinsClaimGameLaunchModal
                            window.postMessage({type: "successful-deposit", value: depositValue});
                            console.log('Close banking window!')
                        }

                        if (failActionsToWatch.includes(newAction)) {
                            sendDepositTrackingEvent(false, depositValue);
                        }
                    }

                    sendLogglyEventBankingEventListener('Message from the banking client', parsedMessage);
                }
            } catch (error) {
                sendLogglyEventBankingEventListener('Error in bankingModalListener', error);
            }
        }
    };

    useEffect(() => {
        window.addEventListener('message', listenForBankingWindowEvents, false);

        return () => {
            window.removeEventListener('message', listenForBankingWindowEvents, false);
        }
    }, []);

    return null;
};

const BankingEventListener = () => (
    <ErrorBoundary>
        <_BankingEventListener/>
    </ErrorBoundary>
);

export default BankingEventListener;
